<template>
  <div class="page-col">
    <div class="page-main p-3">
      <div class="dc-header shadow-sm mb-3">
        <div class="d-flex align-items-center">
          <div class="dc-header-icon mr-3">
            <i class="fa fa-clipboard"></i>
          </div>
          <div>
            <h4 class="mb-0 font-weight-bold">Conferencia de Devolucoes</h4>
            <small class="text-muted">Mercadorias devolvidas aguardando conferencia fisica</small>
          </div>
        </div>
        <div class="d-flex align-items-center">
          <b-button variant="outline-secondary" pill class="mr-2" @click="$router.push('/faturamento/devolucao/index')">
            <i class="fa fa-arrow-left mr-1"></i> Devolucoes
          </b-button>
          <b-button variant="primary" pill :disabled="loading" @click="carregar">
            <i class="fa fa-refresh mr-1" :class="{ 'fa-spin': loading }"></i> Atualizar
          </b-button>
        </div>
      </div>

      <b-row class="mb-3">
        <b-col md="3">
          <div class="dc-stat">
            <small>Pendentes</small>
            <strong>{{ devolucoes.length }}</strong>
          </div>
        </b-col>
        <b-col md="3">
          <div class="dc-stat">
            <small>Itens</small>
            <strong>{{ totalItens }}</strong>
          </div>
        </b-col>
        <b-col md="3">
          <div class="dc-stat">
            <small>Volumes previstos</small>
            <strong>{{ formatarQuantidade(totalPecasNfe, 0) }}</strong>
          </div>
        </b-col>
        <b-col md="3">
          <div class="dc-stat">
            <small>Etiquetas bipadas</small>
            <strong>{{ totalEtiquetasConferidas }}</strong>
          </div>
        </b-col>
      </b-row>

      <div class="dc-filter mb-3">
        <i class="fa fa-search text-muted mr-2"></i>
        <b-form-input
          v-model="filtro"
          class="border-0"
          placeholder="Filtrar por cliente, venda ou devolucao..."
          @input="aplicarFiltro"
        />
      </div>

      <div class="bg-white rounded shadow-sm border">
        <b-table
          :items="devolucoesFiltradas"
          :fields="fields"
          :busy="loading"
          responsive
          hover
          show-empty
          empty-text="Nenhuma devolucao aguardando conferencia"
          class="mb-0"
        >
          <template #cell(ID)="data">
            <span class="font-weight-bold">#{{ data.value }}</span>
          </template>

          <template #cell(CLIENTE_NOME)="data">
            <div class="font-weight-bold">{{ data.value || '-' }}</div>
            <small class="text-muted">Venda #{{ data.item.VENDA_ID || '-' }}</small>
          </template>

          <template #cell(STATUS)="data">
            <b-badge :variant="data.value === '3' ? 'primary' : 'info'">
              {{ data.value === '3' ? 'Em conferencia' : 'Aguardando' }}
            </b-badge>
          </template>

          <template #cell(PROGRESSO)="data">
            <div class="d-flex align-items-center">
              <b-progress :value="progresso(data.item)" max="100" height="8px" class="flex-grow-1 mr-2" />
              <span class="text-muted small">{{ progresso(data.item) }}%</span>
            </div>
            <small class="text-muted">
              {{ data.item.TOTAL_ETIQUETAS_CONFERIDAS || 0 }} / {{ formatarQuantidade(data.item.TOTAL_PECAS_NFE, 0) }}
            </small>
          </template>

          <template #cell(ACOES)="data">
            <div class="d-flex justify-content-end">
              <b-button size="sm" variant="primary" pill class="mr-1" @click="abrir(data.item)">
                <i class="fa fa-clipboard mr-1"></i> Conferir
              </b-button>
              <b-button size="sm" variant="success" pill :disabled="!temRecepcao(data.item)" @click="finalizar(data.item)">
                <i class="fa fa-check mr-1"></i> Finalizar
              </b-button>
            </div>
          </template>
        </b-table>
      </div>

      <devolucao-conferencia-modal
        v-if="devolucaoSelecionada"
        ref="modalConferencia"
        :devolucao-id="devolucaoSelecionada.ID"
        @atualizado="carregar"
        @concluido="carregar"
        @closed="devolucaoSelecionada = null"
      />
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import Tools from '@/tools/tools.js'
import DevolucaoConferenciaModal from './DevolucaoConferenciaModal.vue'

export default {
  name: 'DevolucaoConferenciaListagem',
  components: {
    DevolucaoConferenciaModal
  },
  data() {
    return {
      loading: false,
      devolucoes: [],
      devolucoesFiltradas: [],
      devolucaoSelecionada: null,
      filtro: '',
      fields: [
        { key: 'ID', label: 'Devolucao', sortable: true },
        { key: 'CLIENTE_NOME', label: 'Cliente', sortable: true },
        {
          key: 'DATA_CADASTRO',
          label: 'Data',
          sortable: true,
          formatter: value => value ? moment(value).format('DD/MM/YYYY HH:mm') : ''
        },
        { key: 'STATUS', label: 'Status', sortable: true },
        { key: 'PROGRESSO', label: 'Progresso' },
        { key: 'ACOES', label: '', tdClass: 'text-right' }
      ]
    }
  },
  computed: {
    totalItens() {
      return this.devolucoes.reduce((acc, item) => acc + (Number(item.TOTAL_ITENS) || 0), 0)
    },
    totalPecasNfe() {
      return this.devolucoes.reduce((acc, item) => acc + (Number(item.TOTAL_PECAS_NFE) || 0), 0)
    },
    totalEtiquetasConferidas() {
      return this.devolucoes.reduce((acc, item) => acc + (Number(item.TOTAL_ETIQUETAS_CONFERIDAS) || 0), 0)
    }
  },
  mounted() {
    this.carregar()
  },
  methods: {
    extrairErro(err) {
      const data = err && err.response && err.response.data
      if (data && typeof data === 'object') return data.message || data.erro || data.mensagem || JSON.stringify(data)
      return data || (err && err.message) || err
    },
    async carregar() {
      this.loading = true
      try {
        const response = await this.$http.get('/api/protected/devolucao/conferencia/pendentes')
        this.devolucoes = response.data || []
        this.aplicarFiltro()
      } catch (err) {
        Tools.exibirMensagem(this.$store, 'Erro', 'danger', this.extrairErro(err))
      } finally {
        this.loading = false
      }
    },
    aplicarFiltro() {
      const termo = String(this.filtro || '').trim().toLowerCase()
      if (!termo) {
        this.devolucoesFiltradas = [...this.devolucoes]
        return
      }
      this.devolucoesFiltradas = this.devolucoes.filter(item => {
        return String(item.ID).includes(termo) ||
          String(item.VENDA_ID || '').includes(termo) ||
          String(item.CLIENTE_NOME || '').toLowerCase().includes(termo)
      })
    },
    abrir(devolucao) {
      this.devolucaoSelecionada = devolucao
      this.$nextTick(() => this.$refs.modalConferencia.abrir())
    },
    async finalizar(devolucao) {
      const confirmar = await this.$bvModal.msgBoxConfirm(
        `Deseja finalizar a conferencia da devolucao #${devolucao.ID}?`,
        {
          title: 'Finalizar conferencia',
          okTitle: 'SIM, FINALIZAR',
          cancelTitle: 'CANCELAR',
          okVariant: 'success',
          centered: true
        }
      )
      if (!confirmar) return

      this.loading = true
      try {
        await this.$http.post('/api/protected/devolucao/conferencia/finalizar', {
          devolucaoId: devolucao.ID,
          usuario: this.$store.state.usuario.NOME
        })
        Tools.exibirMensagem(this.$store, 'Conferencia', 'success', 'Conferencia finalizada.')
        await this.carregar()
      } catch (err) {
        Tools.exibirMensagem(this.$store, 'Erro', 'danger', this.extrairErro(err))
      } finally {
        this.loading = false
      }
    },
    progresso(item) {
      const total = Number(item.TOTAL_PECAS_NFE) || 0
      const recebido = Number(item.TOTAL_ETIQUETAS_CONFERIDAS) || 0
      if (total <= 0) return recebido > 0 ? 100 : 0
      return Math.min(100, Math.round((recebido / total) * 100))
    },
    temRecepcao(item) {
      return (Number(item.TOTAL_ETIQUETAS_CONFERIDAS) || 0) > 0
    },
    formatarQuantidade(valor, casas = 3) {
      return (Number(valor) || 0).toLocaleString('pt-BR', {
        minimumFractionDigits: casas,
        maximumFractionDigits: casas
      })
    }
  }
}
</script>

<style scoped>
.dc-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 16px;
}

.dc-header-icon {
  width: 44px;
  height: 44px;
  border-radius: 8px;
  background: #0f766e;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
}

.dc-stat {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 14px;
}

.dc-stat small {
  color: #64748b;
  display: block;
  font-weight: 700;
  text-transform: uppercase;
  font-size: 11px;
}

.dc-stat strong {
  display: block;
  margin-top: 4px;
  font-size: 22px;
  color: #0f172a;
}

.dc-filter {
  display: flex;
  align-items: center;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 6px 10px;
}
</style>



// WEBPACK FOOTER //
// src/views/faturamento/devolucao/DevolucaoConferenciaListagem.vue